import { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-reveal wrapper. Uses IntersectionObserver + CSS classes.
 * Replaces framer-motion whileInView which is being disrupted by the
 * visual-edits Babel plugin in development.
 */
export default function Reveal({
  as: Tag = "div",
  children,
  delay = 0,
  className = "",
  y = 24,
  once = true,
  amount = 0.15,
  duration = 0.85,
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setVisible(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) io.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold: amount, rootMargin: "0px 0px -60px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once, amount]);

  const style = {
    transform: visible ? "translateY(0)" : `translateY(${y}px)`,
    opacity: visible ? 1 : 0,
    transition: `transform ${duration}s cubic-bezier(0.16,1,0.3,1) ${delay}s, opacity ${duration}s ease ${delay}s`,
    willChange: "transform, opacity",
  };

  return (
    <Tag ref={ref} className={className} style={style} {...rest}>
      {children}
    </Tag>
  );
}
